import React, { useEffect, useState } from "react";
import { MapPin, Search, Navigation, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canFinancial } from "@/lib/permissions";
import { fmtDate, fmtMoney } from "@/lib/format";
import { PageHeader, Loading, Field, SectionCard } from "@/components/common";
import { DataTable, Btn, TextInput, Select, Textarea } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";

export default function Sites() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ site_name: "", client_id: "", site_address: "", eircode: "", opening_hours: "", access_instructions: "", parking_info: "", health_safety_notes: "", site_restrictions: "", status: "Active" });

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set("search", search);
      if (clientFilter) p.set("client_id", clientFilter);
      const { data } = await api.get(`/sites?${p}`); setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, clientFilter]);

  const openDetail = async (s) => {
    const { data } = await api.get(`/sites/${s.id}/detail`);
    setDetail(data);
  };

  const save = async () => {
    if (!form.site_name || !form.client_id) return toast.error("Site name and client are required");
    try { await api.post("/sites", form); toast.success("Site created"); setModal(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "site_name", label: "Site", className: "font-medium text-slate-800" },
    { key: "client_name", label: "Client" },
    { key: "site_address", label: "Address", render: (r) => <span className="text-xs text-slate-500">{r.site_address}</span> },
    { key: "eircode", label: "Eircode", render: (r) => <span className="text-xs">{r.eircode}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "nav", label: "", align: "right", render: (r) => (
      <a onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${r.gps_lat},${r.gps_lng}`} className="text-seq-green inline-flex items-center gap-1 text-xs font-medium"><Navigation size={13} /> Map</a>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Sites" subtitle="Client locations, access details and site history" icon={MapPin}
        actions={can(user, "create_sites") && <Btn onClick={() => setModal(true)} data-testid="new-site-btn"><Plus size={16} /> New Site</Btn>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <TextInput data-testid="site-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sites…" className="pl-9" />
        </div>
        <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="max-w-[220px]">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="site-row" onRowClick={openDetail} />}
      </div>

      {detail && <SiteDetailModal detail={detail} onClose={() => setDetail(null)} fin={canFinancial(user)} />}

      <Modal open={modal} onOpenChange={setModal} title="New site" wide
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-site-btn">Save</Btn></>}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Site name" required><TextInput value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} /></Field>
          <Field label="Client" required><Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}><option value="">Select client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}</Select></Field>
          <Field label="Address" className="sm:col-span-2"><TextInput value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} /></Field>
          <Field label="Eircode"><TextInput value={form.eircode} onChange={(e) => setForm({ ...form, eircode: e.target.value })} /></Field>
          <Field label="Opening hours"><TextInput value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })} /></Field>
          <Field label="Access instructions" className="sm:col-span-2"><Textarea rows={2} value={form.access_instructions} onChange={(e) => setForm({ ...form, access_instructions: e.target.value })} /></Field>
          <Field label="Health & safety notes" className="sm:col-span-2"><Textarea rows={2} value={form.health_safety_notes} onChange={(e) => setForm({ ...form, health_safety_notes: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

function SiteDetailModal({ detail, onClose, fin }) {
  const s = detail.site;
  return (
    <Modal open onOpenChange={onClose} title={s.site_name} wide description={s.site_address} testId="site-detail-modal">
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Info label="Eircode" value={s.eircode} />
        <Info label="Opening hours" value={s.opening_hours} />
        <Info label="Access" value={s.access_instructions} />
        <Info label="Parking" value={s.parking_info} />
        <Info label="Health & safety" value={s.health_safety_notes} />
        <Info label="Restrictions" value={s.site_restrictions} />
      </div>
      <div className="flex gap-2">
        <a target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${s.gps_lat},${s.gps_lng}`} className="text-xs inline-flex items-center gap-1 text-seq-green font-medium"><Navigation size={13} /> Google Maps</a>
        <a target="_blank" rel="noreferrer" href={`https://maps.apple.com/?ll=${s.gps_lat},${s.gps_lng}`} className="text-xs inline-flex items-center gap-1 text-slate-500 font-medium">Apple Maps</a>
      </div>
      <SectionCard title={`Visit history (${detail.visits.length})`} noPad>
        <DataTable rows={detail.visits.slice(0, 20)} columns={[
          { key: "job_title", label: "Job", className: "font-medium text-slate-700" },
          { key: "scheduled_date", label: "Date", render: (r) => fmtDate(r.scheduled_date) },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ...(fin ? [{ key: "charge", label: "Charge", align: "right", render: (r) => fmtMoney(r.charge) }] : []),
        ]} />
      </SectionCard>
    </Modal>
  );
}
const Info = ({ label, value }) => (<div><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-slate-700 mt-0.5">{value || "—"}</div></div>);
